import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import {
  Sheet as ShadSheet,
  SheetContent as ShadSheetContent,
  SheetHeader as ShadSheetHeader,
  SheetTitle as ShadSheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import AddTruckForm from "@/rmpm/components/add-truck-form"
import { Schema } from "@/rmpm/lib/schema"
import {
  createRMPMProductSheet,
  createRMPMSheet,
  loadSchemas,
  RMPMSheetType,
} from "@/services/rmpm"
import {
  handleApiError,
  createBatchResult,
  showBatchResultToast,
} from "@/services/error-handler"
import useStore from "@/store"
import { PMTruck, RMTruck } from "@/store/rmpm"
import { differenceBy } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export interface AddTruckSheetProps {
  open?: boolean
  onClose?: () => void
}

export function AddTruckSheet({ open, onClose }: AddTruckSheetProps) {
  const navigate = useNavigate()
  const [rmTrucks, setRMTrucks] = useState<RMTruck[]>([])
  const [pmTrucks, setPMTrucks] = useState<PMTruck[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const rmpmStore = useStore.use.rmpm()

  const defaultRmTrucks = useMemo(() => {
    return toRmTruckOptions(rmpmStore.trucks.rm)
  }, [rmpmStore.trucks.rm])

  const defaultPmTrucks = useMemo(() => {
    return toPmTruckOptions(rmpmStore.trucks.pm)
  }, [rmpmStore.trucks.pm])

  function toRmTruckOptions(schemas: Schema[]) {
    return schemas
    .filter(schema => !schema.isHandover)
    .map((truck) => ({
      id: truck.id || "",
      truckNumber: (truck.properties?.truckNumber.const as string) || "",
      products:
        truck.properties?.products.items?.map(
          (product) => (product.properties?.id.const as string) || ""
        ) || [],
    }))
  }

  function toPmTruckOptions(schemas: Schema[]) {
    return schemas
    .filter(schema => !schema.isHandover)
    .map((truck) => ({
      id: truck.id || "",
      pmType:
        (truck.properties?.general.items?.at(0)?.properties?.value
          .default as string) || "",
      truckNumber: (truck.properties?.truckNumber.const as string) || "",
      products:
        truck.properties?.products.items?.map(
          (product) => (product.properties?.id.const as string) || ""
        ) || [],
    }))
  }

  function handleOpenChange(open: boolean) {
    if (!open && onClose) {
      onClose()
    }
  }

  async function handleAddTrucks() {
    setIsLoading(true)

    // Get new trucks that don't already exist (outside try for catch block access)
    const newRmTrucks = differenceBy(rmTrucks, defaultRmTrucks, (truck) => truck.truckNumber)
    const newPmTrucks = differenceBy(pmTrucks, defaultPmTrucks, (truck) => truck.truckNumber)

    try {
      // Track overall batch operation for trucks
      const truckBatchResult = createBatchResult<string>(newRmTrucks.length + newPmTrucks.length)

      // Create sheets on server for each new RM truck
      const rmTrucksWithSheetIds: RMTruck[] = []
      for (const truck of newRmTrucks) {
        if (!truck.truckNumber) continue

        try {
          const response = await createRMPMSheet({
            truckNumber: truck.truckNumber,
            truckType: "RM" as RMPMSheetType,
          })

          // Track product creation for this truck
          const productBatchResult = createBatchResult<string>(truck.products.length)

          // Create product sheets for each product in this truck
          const productSheetIds: Record<string, string> = {}
          for (const productId of truck.products) {
            const productDetails = rmpmStore.allProducts.rmProducts.find(p => p.id === productId)
            if (productDetails) {
              try {
                const productResponse = await createRMPMProductSheet(response.sheetId, {
                  productId: productDetails.id,
                  productName: productDetails.name,
                })
                productSheetIds[productId] = productResponse.productSheetId
                productBatchResult.successful.push(productId)
                productBatchResult.successCount++
              } catch (err) {
                const errorResult = await handleApiError(
                  err,
                  {
                    operation: `create product sheet for ${productDetails.name}`,
                    context: { truckNumber: truck.truckNumber, productId },
                    showToast: false, // Will show aggregate toast
                  },
                  navigate
                )
                productBatchResult.failed.push({
                  item: productId,
                  error: errorResult.userMessage,
                })
                productBatchResult.failureCount++
              }
            }
          }

          // Show warning if some products failed for this truck
          if (productBatchResult.failureCount > 0 && productBatchResult.successCount > 0) {
            toast.warning(
              `Truck ${truck.truckNumber}: Created ${productBatchResult.successCount} of ${productBatchResult.totalAttempted} products`,
              { duration: 5000 }
            )
          }

          // Only include products that successfully had their sheets created
          const successfulProducts = truck.products.filter(productId => productId in productSheetIds)

          rmTrucksWithSheetIds.push({
            ...truck,
            products: successfulProducts,
            sheetId: response.sheetId,
            productSheetIds,
          } as RMTruck & { productSheetIds: Record<string, string> })

          // Track truck success
          truckBatchResult.successful.push(truck.truckNumber)
          truckBatchResult.successCount++
        } catch (err) {
          const errorResult = await handleApiError(
            err,
            {
              operation: `create sheet for RM truck ${truck.truckNumber}`,
              context: { truckNumber: truck.truckNumber, truckType: "RM" },
              showToast: false, // Will show aggregate toast
            },
            navigate
          )

          // Track truck failure
          truckBatchResult.failed.push({
            item: truck.truckNumber,
            error: errorResult.userMessage,
          })
          truckBatchResult.failureCount++

          // Continue with other trucks even if one fails
        }
      }

      // Create sheets on server for each new PM truck
      const pmTrucksWithSheetIds: PMTruck[] = []
      for (const truck of newPmTrucks) {
        if (!truck.truckNumber) continue

        const pmSheetType: RMPMSheetType = truck.pmType === "LAMINATE" ? "PM_LAMINATE" : "PM_CARTON"

        try {
          const response = await createRMPMSheet({
            truckNumber: truck.truckNumber,
            truckType: pmSheetType,
          })

          // Track product creation for this truck
          const productBatchResult = createBatchResult<string>(truck.products.length)

          // Create product sheets for each product in this truck
          const productSheetIds: Record<string, string> = {}
          for (const productId of truck.products) {
            const productDetails = rmpmStore.allProducts.pmProducts.find(p => p.id === productId)
            if (productDetails) {
              try {
                const productResponse = await createRMPMProductSheet(response.sheetId, {
                  productId: productDetails.id,
                  productName: productDetails.name,
                  market: "DOMESTIC",
                })
                productSheetIds[productId] = productResponse.productSheetId
                productBatchResult.successful.push(productId)
                productBatchResult.successCount++
              } catch (err) {
                const errorResult = await handleApiError(
                  err,
                  {
                    operation: `create product sheet for ${productDetails.name}`,
                    context: { truckNumber: truck.truckNumber, productId },
                    showToast: false, // Will show aggregate toast
                  },
                  navigate
                )
                productBatchResult.failed.push({
                  item: productId,
                  error: errorResult.userMessage,
                })
                productBatchResult.failureCount++
              }
            }
          }

          // Show warning if some products failed for this truck
          if (productBatchResult.failureCount > 0 && productBatchResult.successCount > 0) {
            toast.warning(
              `Truck ${truck.truckNumber}: Created ${productBatchResult.successCount} of ${productBatchResult.totalAttempted} products`,
              { duration: 5000 }
            )
          }

          // Only include products that successfully had their sheets created
          const successfulProducts = truck.products.filter(productId => productId in productSheetIds)

          pmTrucksWithSheetIds.push({
            ...truck,
            products: successfulProducts,
            sheetId: response.sheetId,
            productSheetIds,
          } as PMTruck & { productSheetIds: Record<string, string> })

          // Track truck success
          truckBatchResult.successful.push(truck.truckNumber)
          truckBatchResult.successCount++
        } catch (err) {
          const errorResult = await handleApiError(
            err,
            {
              operation: `create sheet for PM truck ${truck.truckNumber}`,
              context: { truckNumber: truck.truckNumber, truckType: pmSheetType },
              showToast: false, // Will show aggregate toast
            },
            navigate
          )

          // Track truck failure
          truckBatchResult.failed.push({
            item: truck.truckNumber,
            error: errorResult.userMessage,
          })
          truckBatchResult.failureCount++

          // Continue with other trucks even if one fails
        }
      }

      // Load schemas with the trucks that have server-generated sheetIds
      await loadSchemas(rmTrucksWithSheetIds, pmTrucksWithSheetIds)

      // Show aggregate result for all truck operations
      if (truckBatchResult.totalAttempted > 0) {
        showBatchResultToast(truckBatchResult, "truck")
      }

      // Close sheet only if at least one truck succeeded
      if (onClose && truckBatchResult.successCount > 0) {
        onClose()
      }
    } catch (err) {
      await handleApiError(
        err,
        {
          operation: "add trucks",
          context: { rmTruckCount: newRmTrucks.length, pmTruckCount: newPmTrucks.length },
        },
        navigate
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      setRMTrucks(toRmTruckOptions(rmpmStore.trucks.rm))
      setPMTrucks(toPmTruckOptions(rmpmStore.trucks.pm))
    }
  }, [open])

  return (
    <ShadSheet open={open} onOpenChange={handleOpenChange}>
      <ShadSheetContent
        side="bottom"
        className="shadow-drop-shadow mx-auto max-w-md rounded-t-lg"
      >
        <ShadSheetHeader>
          <ShadSheetTitle className="text-xl">Add Trucks</ShadSheetTitle>
        </ShadSheetHeader>

        <SheetDescription className="sr-only">
          Select trucks to add
        </SheetDescription>

        <div className="flex flex-col justify-center gap-5 pb-8">
          <AddTruckForm
            rmTrucks={rmTrucks}
            pmTrucks={pmTrucks}
            defaultRmTrucks={defaultRmTrucks}
            defaultPmTrucks={defaultPmTrucks}
            onChangePmTrucks={setPMTrucks}
            onChangeRmTrucks={setRMTrucks}
          />
        </div>

        <Bottombar className="flex gap-4 bg-white sticky bottom-0">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Back
          </Button>
          <Button
            variant="default"
            className="flex-1"
            onClick={handleAddTrucks}
            disabled={isLoading}
          >
            {isLoading ? "Adding..." : "Add Trucks"}
          </Button>
        </Bottombar>
      </ShadSheetContent>
    </ShadSheet>
  )
}
